/** Mockup 03 · Etapa 2 concluída — A pelo código · B pelo telefone (reserva). */
import { DEFAULT_GAMIFICATION_RULES } from '@troia-v3/types';
import React from 'react';

import { Button, ModalContent, ModalFooter } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { formatPhoneNational } from '@/shared/utils/phone';

import { type CaptureStepActions, leadHref } from './CaptureFilledStep';
import { CaptureStepper, buildCaptureSteps } from './CaptureStepper';
import { CaptureDoneHead, CaptureKv, CapturePointsPill } from './captureUi';

const capturePoints = DEFAULT_GAMIFICATION_RULES.find((r) => r.key === 'lead_captured')?.points ?? 1;
const formPoints = DEFAULT_GAMIFICATION_RULES.find((r) => r.key === 'form_completed')?.points ?? 3;
const formatTime = (iso: string | undefined): string => (iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '');

export const CaptureConfirmedStep: React.FC<CaptureStepActions> = ({ session, onCancel, onSkip, onContinue, busy }) => {
  // `formConfigured` é sobre o FUNIL (mantém o passo "Formulário" no stepper mesmo depois de
  // preenchido — sessão retomada em 'form_completed' não pode "perder" a etapa concluída).
  // `hasForm` é sobre a AÇÃO ainda pendente: com `formCompletedAt` já setado o backend rejeita
  // skip-form fora de filled/confirmed, então não oferece "Pular formulário" de novo.
  // `wasExistingLead` (D5): o lead já era de outro atendimento. O formulário
  // não é desta sessão — o backend recusa `POST …/form` com 409 (senão a
  // aplicação, a Activity e os 3 pontos cairiam no lead alheio, e o dono real
  // nunca mais pontuaria pelo índice único). Aqui isso vira: sem "Pular
  // formulário", sem "Continuar atendimento" — só "Encerrar" (= `complete`).
  const formConfigured = Boolean(session.capture.formId);
  const hasForm = formConfigured && !session.formCompletedAt && !session.wasExistingLead;
  const steps = buildCaptureSteps(true, formConfigured);
  const customer = session.customer;
  const firstName = customer?.name.split(' ')[0] ?? 'O cliente';
  const byPhone = session.confirmedVia === 'phone';
  // Rota real do módulo chat é por path param (`chat/:conversationId`, ver src/lib/router.tsx) — nunca query string.
  const conversationHref = session.conversationId ? `/chat/${session.conversationId}` : undefined;
  // `formCompletedAt` → todos os passos (incl. "Formulário") ficam `done`; sem ele, "Formulário"
  // (se configurado) é o passo `on` corrente — senão o corrente é "WhatsApp".
  const stepperCurrent = session.formCompletedAt ? steps.length : hasForm ? 3 : 2;

  return (
    <>
      <ModalContent>
        <div data-testid="capture-confirmed">
          <CaptureStepper steps={steps} current={stepperCurrent} />
          <CaptureDoneHead tone="wa" icon="message-circle" title="Confirmado no WhatsApp" testId="capture-confirmed-variant" dataVariant={byPhone ? 'phone' : 'code'}>
            {byPhone
              ? <>Chegou uma mensagem deste número no canal às {formatTime(session.confirmedAt)} — sem o código, mas dentro do prazo. Confirmamos pelo telefone.</>
              : <>{firstName} enviou a mensagem às {formatTime(session.confirmedAt)} no canal <b>{session.capture.channelName ?? 'configurado'}</b>. A conversa já está ligada ao lead.</>}
          </CaptureDoneHead>
          <CaptureKv rows={[
            { k: 'Nome', v: customer?.name ?? '—' },
            { k: 'WhatsApp', v: customer ? formatPhoneNational(customer.phone) : '—', mono: true },
            ...(byPhone
              ? [{ k: 'Mensagem', v: <span style={{ fontWeight: 500, color: '#475569' }}>“{session.confirmationText ?? ''}”</span> }]
              : [
                  { k: 'Código', v: session.code, mono: true },
                  { k: 'Conversa', v: (
                    <span className="inline-flex items-center gap-1.5">
                      <span className="inline-block w-[7px] h-[7px] rounded-full" style={{ background: '#10b981' }} />
                      Aberta
                      {conversationHref && <a href={conversationHref} target="_blank" rel="noreferrer" className="inline-flex ml-0.5" style={{ color: '#7c3aed' }} data-testid="capture-open-conversation"><Icon name="external-link" size={14} /></a>}
                    </span>
                  ) },
                ]),
          ]} />
          {!session.wasExistingLead && (
            <div className="flex items-center gap-2.5 mt-3 flex-wrap">
              <CapturePointsPill testId="capture-confirmed-points">+{capturePoints} ponto · lead captado e confirmado</CapturePointsPill>
            </div>
          )}
          {hasForm && !byPhone && (
            <div className="mt-3 flex items-center gap-2.5 rounded-[10px] text-xs" style={{ background: '#f8fafc', border: '1px solid #eef1f5', padding: '10px 12px', color: '#64748b' }}>
              <Icon name="list" size={16} style={{ color: '#7c3aed' }} />
              <div>
                <div className="font-semibold" style={{ color: '#1e293b' }}>Próximo passo: formulário de captura</div>
                <div>Perguntas rápidas sobre o que o cliente procura (≈ 1 min). Vale <b>+{formPoints} pontos</b>.</div>
              </div>
            </div>
          )}
          <a href={leadHref(session)} target="_blank" rel="noreferrer" className="sr-only" data-testid="capture-open-lead">Abrir lead</a>
        </div>
      </ModalContent>
      <ModalFooter>
        <Button variant="ghost" icon="x" loading={busy} onClick={onCancel} data-testid="capture-filled-cancel">Cancelar</Button>
        <span className="flex-1" />
        {session.wasExistingLead ? (
          // D5: nada a continuar nesta sessão — `onContinue` cai direto no `complete`
          // (o `handleContinue` da modal não abre o formulário com `wasExistingLead`).
          <Button variant="primary" loading={busy} onClick={onContinue} data-testid="capture-continue">Encerrar</Button>
        ) : hasForm ? (
          <>
            <Button variant="link" loading={busy} onClick={onSkip} data-testid="capture-skip">Pular formulário</Button>
            <Button variant="primary" icon="arrow-right" iconPosition="right" loading={busy} onClick={onContinue} data-testid="capture-continue">Continuar atendimento</Button>
          </>
        ) : (
          <Button variant="primary" icon="arrow-right" iconPosition="right" loading={busy} onClick={onContinue} data-testid="capture-continue">Continuar atendimento</Button>
        )}
      </ModalFooter>
    </>
  );
};
